import React, { useState } from 'react';
import { X, Lightbulb, Video, Building2, Film } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card } from './ui/Card';
import { SwipefileReferencePicker } from './SwipefileReferencePicker';
import { cn } from '../lib/utils';
interface SwipefileReference {
  id: string;
  title: string;
  thumbnail: string;
  topic: string;
}
interface NewIdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (idea: {
    title: string;
    description: string;
    brandProfile: string;
    formatProfile: string;
    references: SwipefileReference[];
  }) => void;
}
// Mock swipefile data - Banking focused
const MOCK_SWIPEFILE: SwipefileReference[] = [{
  id: '1',
  title: 'How Compound Interest Actually Works',
  thumbnail: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&auto=format&fit=crop&q=60',
  topic: 'Financial Education'
}, {
  id: '2',
  title: '5 Banking Mistakes Gen Z Makes',
  thumbnail: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=60',
  topic: 'Gen Z Banking'
}, {
  id: '3',
  title: 'Understanding Your Credit Score',
  thumbnail: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60',
  topic: 'Credit & Loans'
}, {
  id: '4',
  title: 'Savings Hacks Nobody Tells You',
  thumbnail: 'https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?w=800&auto=format&fit=crop&q=60',
  topic: 'Savings Tips'
}, {
  id: '5',
  title: "What Banks Don't Want You to Know",
  thumbnail: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=60',
  topic: 'Banking Secrets'
}, {
  id: '6',
  title: 'First Time Home Buyer Guide',
  thumbnail: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop&q=60',
  topic: 'Mortgages'
}];
const BRAND_PROFILES = ['einfachbank Professional', 'einfachbank Gen Z'];
const FORMAT_PROFILES: {
  [key: string]: string[];
} = {
  'einfachbank Professional': ['Talking Head', 'Sketch Explainer'],
  'einfachbank Gen Z': ['Whiteboard Session', 'Casual Talking Head']
};
export function NewIdeaModal({
  isOpen,
  onClose,
  onSubmit
}: NewIdeaModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [brandProfile, setBrandProfile] = useState('');
  const [formatProfile, setFormatProfile] = useState('');
  const [selectedReferences, setSelectedReferences] = useState<SwipefileReference[]>([]);
  if (!isOpen) return null;
  const availableFormats = brandProfile ? FORMAT_PROFILES[brandProfile] : [];
  const handleBrandChange = (brand: string) => {
    setBrandProfile(brand);
    setFormatProfile(''); // Reset format when brand changes
  };
  const handleSubmit = () => {
    if (!title.trim() || !description.trim() || !brandProfile || !formatProfile) return;
    onSubmit({
      title,
      description,
      brandProfile,
      formatProfile,
      references: selectedReferences
    });
    setTitle('');
    setDescription('');
    setBrandProfile('');
    setFormatProfile('');
    setSelectedReferences([]);
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-507">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-white" data-id="element-508">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" data-id="element-509">
          <div className="flex items-center gap-3" data-id="element-510">
            <div className="h-10 w-10 rounded-lg bg-[#139191]/10 flex items-center justify-center" data-id="element-511">
              <Lightbulb className="h-5 w-5 text-[#139191]" data-id="element-512" />
            </div>
            <div data-id="element-513">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-514">New Idea</h2>
              <p className="text-sm text-gray-500" data-id="element-515">
                Create a new content idea with swipefile references
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-id="element-516">
            <X className="h-5 w-5" data-id="element-517" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-518">
          {/* Brand Profile */}
          <div data-id="element-519">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-520">
              <Building2 className="h-4 w-4 text-[#139191]" data-id="element-521" />
              Brand Profile *
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-522">
              {BRAND_PROFILES.map(brand => <button key={brand} onClick={() => handleBrandChange(brand)} className={cn('px-4 py-2 rounded-lg text-sm font-medium transition-all border', brandProfile === brand ? 'bg-[#139191] text-white border-[#139191]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#139191] hover:text-[#139191]')} data-id="element-523">
                  {brand}
                </button>)}
            </div>
          </div>

          {/* Format Profile */}
          {brandProfile && <div data-id="element-524">
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-525">
                <Film className="h-4 w-4 text-purple-600" data-id="element-526" />
                Format Profile *
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-527">
                {availableFormats.map(format => <button key={format} onClick={() => setFormatProfile(format)} className={cn('px-4 py-2 rounded-lg text-sm font-medium transition-all border', formatProfile === format ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-200 hover:border-purple-600 hover:text-purple-600')} data-id="element-528">
                    {format}
                  </button>)}
              </div>
            </div>}

          {/* Idea Title */}
          <div data-id="element-529">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-530">
              Idea Title *
            </label>
            <Input placeholder="e.g., Credit Score Myths Debunked" value={title} onChange={e => setTitle(e.target.value)} className="text-base" data-id="element-531" />
          </div>

          {/* Idea Description */}
          <div data-id="element-532">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-533">
              Description *
            </label>
            <textarea placeholder="Describe your content idea in detail..." value={description} onChange={e => setDescription(e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#139191] focus:border-transparent resize-none text-sm" data-id="element-534" />
          </div>

          {/* Swipefile References */}
          <div data-id="element-535">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-536">
              <Video className="h-4 w-4 text-[#139191]" data-id="element-537" />
              Add Swipefile References (Optional)
            </label>
            <p className="text-xs text-gray-500 mb-4" data-id="element-538">
              Select videos from your swipefile to inspire this idea. The AI
              will analyze these references when generating hooks and concepts.
            </p>
            <SwipefileReferencePicker availableReferences={MOCK_SWIPEFILE} selectedReferences={selectedReferences} onSelectionChange={setSelectedReferences} data-id="element-539" />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50" data-id="element-540">
          <p className="text-sm text-gray-500" data-id="element-541">
            {selectedReferences.length > 0 && `${selectedReferences.length} reference${selectedReferences.length > 1 ? 's' : ''} selected`}
          </p>
          <div className="flex gap-3" data-id="element-542">
            <Button variant="outline" onClick={onClose} data-id="element-543">
              Cancel
            </Button>
            <Button className="bg-[#139191] hover:bg-[#139191]/90 text-white" onClick={handleSubmit} disabled={!title.trim() || !description.trim() || !brandProfile || !formatProfile} data-id="element-544">
              Create Idea
            </Button>
          </div>
        </div>
      </Card>
    </div>;
}